import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OrgSettings } from '@go-modules/interfaces/groups/org-settings';
import type { GuestReviewer } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { noop } from 'angular';
import { sessionToken, Session as SessionModel } from 'go-modules/models/session/session.service';
import { BehaviorSubject, map, Observable, Subject, takeUntil } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogData } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { VideoShareDialogService } from '../dialogs/video-share-dialog/video-share-dialog.service';
import { Group as GroupModel } from 'go-modules/models/group-dep/group.factory';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';

@Component({
	selector: 'video-reviewers',
	templateUrl: './video-reviewers.component.html',
	styleUrls: ['./video-reviewers.component.scss'],
	imports: [
		NgIf,
		TranslateModule,
		MatTooltipModule,
		AsyncPipe,
		NgForOf
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoReviewersComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public session: ReturnType<typeof SessionModel>;
	@Input() public group: ReturnType<typeof GroupModel>;
	@Input() public orgSettings: OrgSettings;
	@Output() public onDeleteReviewer = new EventEmitter();

	public allReviewers$ = new BehaviorSubject<GuestReviewer[]>([]);
	public videoReviewers$: Observable<GuestReviewer[]>;
	private destroy$ = new Subject<void>();

	constructor (
		@Inject(sessionToken) private Session: ReturnType<typeof SessionModel>,
		@Inject(userServiceToken) private userService: UserService,
		public ngxVideoShareDialogService: VideoShareDialogService,
		private dialog: MatDialog,
		private translate: TranslateService
	) {}

	public ngOnInit (): void {
		this.setAllReviewers();
		this.videoReviewers$ = this.allReviewers$.asObservable().pipe(
			takeUntil(this.destroy$),
			map((reviewers: GuestReviewer[]) =>
				reviewers.filter(Boolean).filter((guest) => guest.deleted_at === null || !guest.hasOwnProperty('deleted_at'))
			)
		);
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes && changes.session && changes.session.firstChange === false) {
			this.session.guest_reviewers = changes.session.currentValue.guest_reviewers;
			this.session.internal_reviewers = changes.session.currentValue.internal_reviewers;
			this.setAllReviewers();
		}
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();

		this.allReviewers$.complete();
	}

	public getUserName (guest): string {
		if (guest.user) {
			return guest.user.full_name;
		}

		return guest.full_name ?? '–';
	}

	public getEmail (guest): string {
		if (this.group.hasInstructorRole(true)) {
			return guest.email;
		}
		return (guest.uuid && guest.created_by === this.userService.currentUser.user_id) ? guest.email : '—';
	};

	public getDeleteAriaLabel (guest): string {
		if (guest.user) {
			return this.translate.instant('session-editor-panel_external-reviewers-delete-name', {name: guest.user.full_name});
		}

		return this.translate.instant('group-settings_delete-invite-email', {email: guest.email});
	}

	public removeGuest (guest) {
		if (!guest.user_id) {
			return this.removeInvite(guest);
		}

		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('modal-remove-video-reviewer_title'),
				message: guest.hasOwnProperty('uuid') ?
					this.translate.instant('modal-remove-guest-reviewer_text', {fullname: guest.user.full_name}) :
					this.translate.instant('modal-remove-video-reviewer_text', {fullname: guest.full_name}),
				confirmText: this.translate.instant('common_remove')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().subscribe((confirm) => {
			if (confirm) {
				if (guest.hasOwnProperty('uuid')) {
					this.Session.deleteGuestReviewer({
						session_id: this.session.session_id,
						guest_id: guest.id
					}).$promise
						.then(() => this.removeGuestFromList(guest))
						.catch(() => noop);
				} else {
					this.Session.deleteInternalReviewer({
						session_id: this.session.session_id,
						user_id: guest.user_id
					}).$promise
						.then(() => this.removeGuestFromList(guest))
						.catch(() => noop);
				}
			}
		});
	}

	private removeInvite (guest: GuestReviewer) {
		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('group-settings_delete-invite'),
				message: this.translate.instant('modal-remove-invite_sure-delete-invite', { email: guest.email }),
				confirmText: this.translate.instant('common_delete'),
				cancelText: this.translate.instant('common_cancel')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().subscribe((confirm) => {
			if (confirm) {
				return this.Session.deleteGuestReviewer({
					session_id: this.session.session_id,
					guest_id: guest.id
				}).$promise
					.then(() => this.removeGuestFromList(guest))
					.catch(() => noop);
			}
		});
	}

	private setAllReviewers () {
		this.allReviewers$.next(this.session.guest_reviewers.concat(this.session.internal_reviewers));
	}

	private removeGuestFromList (guest: GuestReviewer) {
		const currentReviewers = this.allReviewers$.value;
		let updatedReviewers = [];

		// internal reviewers don't have id but has user_id
		if (!guest.id) {
			updatedReviewers = currentReviewers.map((reviewer) =>
				reviewer.user_id === guest.user_id
					? { ...reviewer, deleted_at: new Date() }
					: reviewer
			);
		} else {
			updatedReviewers = currentReviewers.map((reviewer) =>
				reviewer.id === guest.id
					? { ...reviewer, deleted_at: new Date() }
					: reviewer
			);
		}

		this.allReviewers$.next(updatedReviewers);
		this.onDeleteReviewer.emit(guest);
	}
}
